/**
 * Per `character-energy-system` — fan battery overlay for the chat page.
 *
 * Mounts on logged-in fan chat surfaces ONLY (NOT the anonymous 智能客服
 * embed). Shows the battery bar, runs the per-minute heartbeat while the
 * tab is focused, and surfaces the idle screen + insufficient-energy +
 * shop modals.
 *
 * Self-contained (inline styles) so it drops onto the existing chat page
 * without touching the embed runtime's CSS.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getEnergy, tickEnergy, resumeEnergy, rechargeEnergy,
  getEnergyShop, purchaseEnergyShop,
  type EnergyView, type EnergyShopItem,
} from '../api/fan-api';

const HEARTBEAT_MS = 60_000;

function barColor(pct: number): string {
  if (pct >= 50) return '#34D399';
  if (pct >= 20) return '#F59E0B';
  return '#F43F5E';
}

const SEGMENTS = 20; // 5% per cell

/**
 * Charging surge: when the target pct JUMPS UP (recharge / shop buy /
 * regen credit), the displayed pct climbs cell-by-cell (45ms per 5%
 * cell) so the segments light sequentially — reads as current flowing
 * into the battery — while the shell glows. Decreases apply instantly
 * (spend feedback is the cell going dark). prefers-reduced-motion
 * skips the climb.
 */
function useChargingPct(target: number): { pct: number; charging: boolean; justFull: boolean } {
  const [pct, setPct] = useState(target);
  const [charging, setCharging] = useState(false);
  // One-shot "topped off" flash when a charge lands exactly on 100%.
  const [justFull, setJustFull] = useState(false);
  const pctRef = useRef(pct);
  pctRef.current = pct;
  useEffect(() => {
    if (!justFull) return;
    const t = setTimeout(() => setJustFull(false), 950);
    return () => clearTimeout(t);
  }, [justFull]);
  useEffect(() => {
    const reduced = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || target <= pctRef.current) {
      setPct(target);
      setCharging(false);
      return;
    }
    setCharging(true);
    const id = setInterval(() => {
      setPct((cur) => {
        const next = Math.min(cur + 100 / SEGMENTS, target);
        if (next >= target) {
          clearInterval(id);
          setCharging(false);
          if (target >= 100) setJustFull(true);
        }
        return next;
      });
    }, 45);
    return () => clearInterval(id);
  }, [target]);
  return { pct, charging, justFull };
}

/** Phone-style battery: slanted parallelogram cells + nub + % overlaid. */
function BatteryGauge({ pct: targetPct }: { pct: number }) {
  const { pct, charging, justFull } = useChargingPct(targetPct);
  const lit = Math.round(pct / (100 / SEGMENTS));
  const col = barColor(pct);
  // Low-battery heartbeat: <20% (and not actively charging) the shell
  // pulses red — a quiet, persistent nudge toward the 充電 button.
  const reduced = typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const low = pct < 20 && pct > 0 && !charging;
  return (
    <div style={{
      ...G.shell,
      ...(justFull ? {
        borderColor: '#6EE7B7',
        animation: 'nx-batt-full 0.95s ease-out 1',
      } : charging ? {
        borderColor: col,
        boxShadow: `0 0 12px ${col}66, inset 0 0 8px ${col}33`,
        transition: 'box-shadow .2s ease, border-color .2s ease',
      } : low ? {
        borderColor: '#F43F5E',
        ...(reduced
          ? { boxShadow: '0 0 10px rgba(244,63,94,0.5)' }
          : { animation: 'nx-batt-low 1.6s ease-in-out infinite' }),
      } : { transition: 'box-shadow .4s ease, border-color .4s ease' }),
    }}>
      {justFull && (
        <style>{`@keyframes nx-batt-full {
          0%   { box-shadow: 0 0 8px rgba(110,231,183,0.4); }
          35%  { box-shadow: 0 0 26px rgba(110,231,183,0.95), inset 0 0 12px rgba(110,231,183,0.4); }
          100% { box-shadow: 0 0 6px rgba(52,211,153,0.25); }
        }`}</style>
      )}
      {low && !reduced && (
        <style>{`@keyframes nx-batt-low {
          0%, 100% { box-shadow: 0 0 4px rgba(244,63,94,0.25); }
          50%      { box-shadow: 0 0 16px rgba(244,63,94,0.8); }
        }`}</style>
      )}
      <div style={G.cells}>
        {Array.from({ length: SEGMENTS }).map((_, i) => (
          <div key={i} style={{ ...G.cell, background: i < lit ? col : 'rgba(255,255,255,0.12)', boxShadow: i < lit ? `0 0 5px ${col}` : 'none' }} />
        ))}
      </div>
      <div style={G.nub} />
      <span style={{
        ...G.label,
        ...(justFull ? { color: '#6EE7B7', textShadow: '0 0 10px #6EE7B7' }
          : charging ? { color: col, textShadow: `0 0 8px ${col}` } : {}),
      }}>
        ⚡ {Math.round(pct)}%{justFull ? ' 滿電!' : charging ? ' 充電中' : ''}
      </span>
    </div>
  );
}

const G: Record<string, React.CSSProperties> = {
  shell: { position: 'relative', flex: 1, display: 'flex', alignItems: 'center', height: 24, padding: '2px 9px 2px 5px', borderRadius: 7, border: '1.5px solid rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.06)' },
  cells: { display: 'flex', gap: 2, height: '100%', alignItems: 'center', flex: 1 },
  cell: { flex: 1, height: '70%', transform: 'skewX(-20deg)', borderRadius: 1.5, transition: 'background .25s ease, box-shadow .25s ease' },
  nub: { position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)', width: 3, height: 10, borderRadius: '0 2px 2px 0', background: 'rgba(255,255,255,0.5)' },
  label: { position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 11, fontWeight: 800, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.9)', letterSpacing: 0.3, pointerEvents: 'none' },
};

/** Keyframes for the gift toss — injected once by the component so it
 *  stays self-contained (no styles.css dependency). Two nested layers:
 *  X drifts linearly while Y plays a gravity arc (decelerate up,
 *  accelerate down) + a landing bounce = a true ballistic toss. */
const GIFT_FLY_CSS = `
@keyframes nx-gift-x { from { transform: translateX(0); } to { transform: translateX(7vw); } }
@keyframes nx-gift-y {
  0%   { transform: translateY(0) scale(1); opacity: 1; }
  55%  { transform: translateY(-46vh) scale(1.15); animation-timing-function: cubic-bezier(0.5, 0, 0.9, 0.6); }
  82%  { transform: translateY(-34vh) scale(1, 0.8); }
  90%  { transform: translateY(-37vh) scale(0.95, 1.05); }
  100% { transform: translateY(-35vh) scale(1); opacity: 0; }
}
.nx-gift-x { animation: nx-gift-x 1.05s linear forwards; }
.nx-gift-y { animation: nx-gift-y 1.05s cubic-bezier(0.2, 0.7, 0.4, 1) forwards; display: inline-block; }
`;

/** Game-style cost float: "-N ⚡" rises 16px and fades over 0.9s,
 *  anchored to the battery bar. Keyed by spend id so consecutive
 *  spends restart the animation. */
function SpendFloat({ amt }: { amt: number }) {
  return (
    <>
      <style>{`@keyframes nx-spend-float {
        0%   { opacity: 0; transform: translateY(2px); }
        18%  { opacity: 1; }
        100% { opacity: 0; transform: translateY(-16px); }
      }`}</style>
      <span style={{
        position: 'absolute', right: 64, top: '100%', marginTop: 2,
        fontSize: 11, fontWeight: 800, color: '#FDA4AF',
        textShadow: '0 1px 4px rgba(0,0,0,0.8)', pointerEvents: 'none',
        animation: 'nx-spend-float 0.9s ease-out forwards', whiteSpace: 'nowrap',
      }}>
        -{amt} ⚡
      </span>
    </>
  );
}

export function EnergyBattery({ characterId, idleImageUrl, readOnly, topBar }: { characterId: string; idleImageUrl?: string; readOnly?: boolean; topBar?: boolean }) {
  const [view, setView] = useState<EnergyView | null>(null);
  const [idle, setIdle] = useState(false);
  const [showInsufficient, setShowInsufficient] = useState(false);
  const [shop, setShop] = useState<EnergyShopItem[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pausedRef = useRef(false); // paused while a modal is up
  // Spend float — game-style "-N ⚡" drifting up from the battery
  // whenever energy drops (message cost, idle drain). Makes every
  // cost legible at the moment it happens.
  const [spendFloat, setSpendFloat] = useState<{ id: number; amt: number } | null>(null);
  const prevEnergyRef = useRef<number | null>(null);
  const spendIdRef = useRef(0);
  useEffect(() => {
    if (!view) return;
    const prev = prevEnergyRef.current;
    prevEnergyRef.current = view.energy;
    if (prev !== null && view.energy < prev) {
      const reduced = typeof window !== 'undefined'
        && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (!reduced) setSpendFloat({ id: ++spendIdRef.current, amt: prev - view.energy });
    }
  }, [view]);
  useEffect(() => {
    if (!spendFloat) return;
    const t = setTimeout(() => setSpendFloat(null), 950);
    return () => clearTimeout(t);
  }, [spendFloat]);

  // Gift toss: after a successful recharge/shop buy the ⚡ arcs from
  // the modal area up to the character, lands with a bounce, then the
  // battery's cell-by-cell charging (#3) takes over. Skipped under
  // prefers-reduced-motion.
  const [giftFly, setGiftFly] = useState(false);

  useEffect(() => {
    if (!giftFly) return;
    const t = setTimeout(() => setGiftFly(false), 1150);
    return () => clearTimeout(t);
  }, [giftFly]);

  const launchGift = useCallback(() => {
    const reduced = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) setGiftFly(true);
  }, []);

  // Energy API unreachable (e.g. prod before the energy backend ships).
  // Batteries are ALWAYS created full server-side and chat isn't gated
  // when the API is absent, so the honest fallback is a full battery
  // with the charge/shop controls hidden.
  const [unavailable, setUnavailable] = useState(false);
  const refresh = useCallback(async () => {
    try {
      setView(await getEnergy(characterId));
      setUnavailable(false);
    } catch { setUnavailable(true); }
  }, [characterId]);

  // Broadcast the energy level so the 3D stage can react (e.g. the
  // tired droop below 20%). Fired on every view change — heartbeat,
  // spend, recharge — so the posture tracks the battery live.
  useEffect(() => {
    if (!view) return;
    window.dispatchEvent(new CustomEvent('nx-energy:level', {
      detail: { characterId, pct: view.pct },
    }));
  }, [view, characterId]);

  useEffect(() => {
    // Opening the chat counts as activity → reset the idle clock so the
    // 10-min idle timer measures from page-entry, NOT a stale lastActivityAt
    // left over from a previous session (which made the idle screen pop up
    // almost immediately). Read-only (market) view never drains/idles.
    if (!readOnly) void resumeEnergy(characterId).catch(() => {});
    void refresh();
  }, [characterId, readOnly, refresh]);

  // Refresh immediately whenever a chat reply lands (energy was just
  // spent) — per-message updates instead of waiting for the heartbeat.
  useEffect(() => {
    const onSpent = (e: Event) => {
      const cid = (e as CustomEvent<{ characterId?: string }>).detail?.characterId;
      if (!cid || cid === characterId) void refresh();
    };
    window.addEventListener('nx-energy:spent', onSpent);
    return () => window.removeEventListener('nx-energy:spent', onSpent);
  }, [characterId, refresh]);

  // Heartbeat — only ticks while focused/visible and not paused by a modal.
  // Disabled in read-only mode (e.g. the market page just SHOWS the level;
  // only the chat page drains energy over time).
  useEffect(() => {
    if (readOnly) return;
    let alive = true;
    const id = setInterval(async () => {
      if (!alive || pausedRef.current) return;
      if (typeof document !== 'undefined' && (document.visibilityState !== 'visible' || !document.hasFocus())) return;
      try {
        const v = await tickEnergy(characterId);
        if (!alive) return;
        setView(v);
        if (v.idle) { setIdle(true); pausedRef.current = true; }
        else if (v.energy <= 0) { setShowInsufficient(true); pausedRef.current = true; }
      } catch { /* ignore transient */ }
    }, HEARTBEAT_MS);
    return () => { alive = false; clearInterval(id); };
  }, [characterId]);

  const onResume = async () => {
    setBusy(true);
    try {
      setView(await resumeEnergy(characterId));
      setIdle(false);
      pausedRef.current = false;
      // Wake-up shake — the 3D stage gives the body a gentle nudge so
      // "she wakes up" instead of the overlay just vanishing.
      window.dispatchEvent(new CustomEvent('nx-energy:resume', { detail: { characterId } }));
    }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const openShop = async () => {
    setBusy(true); setErr(null);
    try { const r = await getEnergyShop(characterId); setShop(r.items); setView(r.energy); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const doRecharge = async (points: number) => {
    setBusy(true); setErr(null);
    try {
      const v = await rechargeEnergy(characterId, points);
      setView(v); setShowInsufficient(false); pausedRef.current = false;
      launchGift();
    } catch (e) {
      const code = (e as { code?: string }).code;
      setErr(code === 'INSUFFICIENT_FUNDS' ? '點數不足,請先儲值' : (e as Error).message);
    } finally { setBusy(false); }
  };

  const buy = async (itemId: string) => {
    setBusy(true); setErr(null);
    try {
      const v = await purchaseEnergyShop(characterId, itemId);
      setView(v); setShop(null); setShowInsufficient(false); pausedRef.current = false;
      launchGift();
    } catch (e) {
      const code = (e as { code?: string }).code;
      setErr(code === 'INSUFFICIENT_FUNDS' ? '點數不足,請先儲值' : (e as Error).message);
    } finally { setBusy(false); }
  };

  // Still LOADING (no data, no error yet) → render nothing; a gauge
  // before the first fetch resolves would misleadingly flash 0%.
  // API UNAVAILABLE → show a full battery (server always creates
  // batteries full; chat isn't energy-gated when the API is absent)
  // with the 充電 button hidden.
  if (!view && !unavailable) return null;

  const apiMissing = !view && unavailable;
  const pct = view?.pct ?? 100;
  const idleImg = view?.idleImageUrl ?? idleImageUrl;

  return (
    <>
      {/* ── Gift toss (recharge success → ⚡ arcs up to the character) */}
      {giftFly && (
        <div style={S.giftWrap} aria-hidden="true">
          <style>{GIFT_FLY_CSS}</style>
          <div className="nx-gift-x">
            <span className="nx-gift-y" style={S.giftEmoji}>⚡</span>
          </div>
        </div>
      )}

      {/* ── Battery bar (phone-style slanted segments) ─────────── */}
      {topBar ? (
        // Full-width top status bar, capped to the character panel width
        // (max-w-md) and centred — the character + icons are shifted down
        // below it via the --nx-batt-h CSS var on the chat container.
        <div style={S.topWrap}>
          <div style={{ ...S.topInner, position: 'relative' }}>
            <BatteryGauge pct={pct} />
            {!apiMissing && <button style={S.chargeBtn} onClick={() => void openShop()} disabled={busy}>充電</button>}
            {spendFloat && <SpendFloat key={spendFloat.id} amt={spendFloat.amt} />}
          </div>
        </div>
      ) : (
        <div style={{ ...S.bar, position: 'absolute' }}>
          <BatteryGauge pct={pct} />
          {!apiMissing && <button style={S.chargeBtn} onClick={() => void openShop()} disabled={busy}>充電</button>}
          {spendFloat && <SpendFloat key={spendFloat.id} amt={spendFloat.amt} />}
        </div>
      )}

      {/* ── Idle screen ──────────────────────────────────────── */}
      {idle && (
        <div style={S.overlay} onClick={() => void onResume()}>
          {/* Sleepy ambience: the card breathes in softly on entry and
              the moon drifts on a slow 3.2s float — resting, not frozen.
              Reduced-motion gets the static card (animations none'd). */}
          <style>{`
            @keyframes nx-idle-card-in { from { opacity: 0; transform: scale(0.94) translateY(6px); } to { opacity: 1; transform: none; } }
            @keyframes nx-idle-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
            @media (prefers-reduced-motion: reduce) {
              .nx-idle-card { animation: none !important; }
              .nx-idle-moon { animation: none !important; }
            }
          `}</style>
          <div
            className="nx-idle-card"
            style={{ ...S.idleCard, animation: 'nx-idle-card-in 0.35s cubic-bezier(0.22, 1, 0.36, 1) both' }}
            onClick={(e) => e.stopPropagation()}
          >
            {idleImg
              ? <img src={idleImg} alt="idle" style={S.idleImg} />
              : (
                <div
                  className="nx-idle-moon"
                  style={{ ...S.idleEmoji, animation: 'nx-idle-float 3.2s ease-in-out infinite', display: 'inline-block' }}
                >🌙</div>
              )}
            <div style={S.idleTitle}>休息一下中…</div>
            <div style={S.idleHint}>離開太久了,電力已暫停消耗</div>
            <button style={S.primaryBtn} onClick={() => void onResume()} disabled={busy}>點擊繼續</button>
          </div>
        </div>
      )}

      {/* ── Insufficient energy ──────────────────────────────── */}
      {showInsufficient && !idle && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <div style={S.idleEmoji}>🪫</div>
            <div style={S.idleTitle}>電力不足</div>
            <div style={S.idleHint}>充飽電才能繼續和 TA 聊天(或等待自動回充)</div>
            {err && <div style={S.err}>{err}</div>}
            <button style={S.primaryBtn} onClick={() => void doRecharge((view?.capacity ?? 100))} disabled={busy}>⚡ 一鍵充滿</button>
            <button style={S.secondaryBtn} onClick={() => void openShop()} disabled={busy}>前往商城</button>
            <button style={S.linkBtn} onClick={() => { setShowInsufficient(false); pausedRef.current = false; }}>稍後再說</button>
          </div>
        </div>
      )}

      {/* ── Shop ─────────────────────────────────────────────── */}
      {shop && (
        <div style={S.overlay} onClick={() => setShop(null)}>
          {/* Same entrance language as everywhere else: the card pops
              in softly, items shelf-fill with a 50ms stagger. */}
          <style>{`
            @keyframes nx-shop-card-in { from { opacity: 0; transform: scale(0.94) translateY(8px); } to { opacity: 1; transform: none; } }
            @keyframes nx-shop-item-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
            @media (prefers-reduced-motion: reduce) {
              .nx-shop-card, .nx-shop-item { animation: none !important; opacity: 1 !important; }
            }
          `}</style>
          <div
            className="nx-shop-card"
            style={{ ...S.shopCard, animation: 'nx-shop-card-in 0.3s cubic-bezier(0.22, 1, 0.36, 1) both' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={S.shopHead}>
              <span style={S.idleTitle}>⚡ 電力商城</span>
              <span style={S.shopBal}>目前 {view?.pct ?? 0}%</span>
            </div>
            {err && <div style={S.err}>{err}</div>}
            <div style={S.shopList}>
              {shop.map((it, i) => (
                <div
                  key={it.id}
                  className="nx-shop-item"
                  style={{
                    ...S.shopItem,
                    animation: 'nx-shop-item-in 0.32s cubic-bezier(0.22, 1, 0.36, 1) backwards',
                    animationDelay: `${Math.min(i, 7) * 0.05}s`,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={S.itemName}>{it.name}</div>
                    {it.description && <div style={S.itemDesc}>{it.description}</div>}
                  </div>
                  <button style={S.buyBtn} onClick={() => void buy(it.id)} disabled={busy}>{it.pricePoints} 點</button>
                </div>
              ))}
            </div>
            <button style={S.linkBtn} onClick={() => setShop(null)}>關閉</button>
          </div>
        </div>
      )}
    </>
  );
}

const S: Record<string, React.CSSProperties> = {
  // Default (market viewport) placement — pinned to the TOP of the
  // character panel, never over the character's body, and capped to the
  // panel width (left/right 12 inside the panel container). Per the
  // operator's rule: 電池放最上面、最多與角色同寬、不得蓋住角色。
  bar: { position: 'absolute', top: 10, left: 12, right: 12, maxWidth: '28rem', margin: '0 auto', zIndex: 40, display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 14, background: 'rgba(4,16,46,0.92)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', boxShadow: '0 4px 14px rgba(0,0,0,0.35)' },
  // Top status-bar mode: spans the panel (max-w-md), pinned to the very
  // top; the chat page reserves space for it via --nx-batt-h.
  topWrap: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 40, display: 'flex', justifyContent: 'center', paddingTop: 'env(safe-area-inset-top)', background: 'rgba(4,12,34,0.94)', borderBottom: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 10px rgba(0,0,0,0.28)' },
  topInner: { width: '100%', maxWidth: '28rem', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px' },
  bolt: { fontSize: 14 },
  track: { flex: 1, height: 10, borderRadius: 999, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 999, transition: 'width .4s ease' },
  pctTxt: { fontSize: 12, color: '#E2ECFB', fontWeight: 600, minWidth: 34, textAlign: 'right' },
  chargeBtn: { fontSize: 12, fontWeight: 700, color: '#04102E', background: '#F59E0B', border: 'none', borderRadius: 999, padding: '4px 10px', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(4,16,46,0.72)', backdropFilter: 'blur(4px)' },
  idleCard: { width: 300, maxWidth: '86vw', textAlign: 'center', padding: 22, borderRadius: 20, background: '#0B1E47', color: '#fff' },
  modal: { width: 300, maxWidth: '86vw', textAlign: 'center', padding: 22, borderRadius: 20, background: '#0B1E47', color: '#fff', display: 'flex', flexDirection: 'column', gap: 10 },
  idleImg: { width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 14 },
  idleEmoji: { fontSize: 52 },
  idleTitle: { fontSize: 20, fontWeight: 700, marginTop: 8 },
  idleHint: { fontSize: 13, color: '#AFC2E4', marginTop: 4 },
  primaryBtn: { marginTop: 14, fontSize: 15, fontWeight: 700, color: '#04102E', background: '#22D3EE', border: 'none', borderRadius: 12, padding: '10px 16px', cursor: 'pointer' },
  secondaryBtn: { fontSize: 14, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 12, padding: '9px 16px', cursor: 'pointer' },
  linkBtn: { fontSize: 12, color: '#8AA0C8', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4 },
  err: { fontSize: 12, color: '#FCA5A5', background: 'rgba(244,63,94,0.12)', borderRadius: 8, padding: '6px 8px' },
  shopCard: { width: 360, maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto', padding: 18, borderRadius: 20, background: '#0B1E47', color: '#fff' },
  shopHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 },
  shopBal: { fontSize: 13, color: '#34D399', fontWeight: 700 },
  shopList: { display: 'flex', flexDirection: 'column', gap: 8 },
  shopItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.06)' },
  // Gift toss anchor — bottom-center (where the modal sat); the nested
  // X/Y animations carry the emoji up to the character viewport.
  giftWrap: { position: 'fixed', left: '50%', bottom: '22%', zIndex: 70, pointerEvents: 'none', marginLeft: -14 },
  giftEmoji: { fontSize: 28, filter: 'drop-shadow(0 4px 10px rgba(245,158,11,0.55))' },
  itemName: { fontSize: 14, fontWeight: 600 },
  itemDesc: { fontSize: 12, color: '#AFC2E4' },
  buyBtn: { fontSize: 13, fontWeight: 700, color: '#04102E', background: '#F59E0B', border: 'none', borderRadius: 999, padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
};
